import { useEffect, useState } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { Shell, PageHeader } from '../components/Shell.jsx';
import { Field, Input, Textarea } from '../components/Field.jsx';
import { Score } from '../components/Score.jsx';

export default function EditPitch() {
  const { id } = useParams();
  const nav = useNavigate();
  const [pitch, setPitch] = useState(null);
  const [subject, setSubject] = useState('');
  const [body, setBody] = useState('');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');
  const [original, setOriginal] = useState({ subject: '', body: '' });

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [id]);

  async function load() {
    try {
      const r = await fetch(`/api/pitches/${id}`, { credentials: 'include' });
      if (!r.ok) { setErr(`could not load pitch (${r.status})`); return; }
      const d = await r.json();
      setPitch(d.pitch);
      setSubject(d.pitch.subject || '');
      setBody(d.pitch.body || '');
      setOriginal({ subject: d.pitch.subject || '', body: d.pitch.body || '' });
    } catch (e) {
      setErr(String(e.message || e));
    }
  }

  const dirty = subject !== original.subject || body !== original.body;
  const wordCount = body.trim().split(/\s+/).filter(Boolean).length;
  const subjLen = subject.length;
  const subjOver = subjLen > 70;
  const wordsOver = wordCount > 180;
  const emDashCount = (subject + ' ' + body).match(/[—–]/g)?.length || 0;

  async function save({ thenApprove } = {}) {
    setBusy(true); setErr('');
    try {
      const r = await fetch(`/api/pitches/${id}`, {
        method: 'PUT', credentials: 'include',
        headers: { 'content-type': 'application/json' },
        body: JSON.stringify({ subject, body, edit_reason: 'manual edit in UI' }),
      });
      if (!r.ok) {
        const d = await r.json().catch(() => ({}));
        setErr(d.message || 'save failed'); return;
      }
      if (thenApprove) {
        const a = await fetch(`/api/pitches/${id}/approve`, {
          method: 'POST', credentials: 'include',
        });
        if (!a.ok) {
          const d = await a.json().catch(() => ({}));
          setErr(d.message || 'approve failed'); return;
        }
        const d = await a.json();
        if (d.sent) {
          if (d.test_redirect) {
            alert(`✓ Approved and sent.\n\nTest mode is on — the email went to ${d.effective_to} instead of the actual prospect (${d.intended_to}). When you're ready to send for real, clear the TEST_SEND_OVERRIDE_TO env var.`);
          } else {
            alert(`✓ Approved and sent to ${d.intended_to}`);
          }
        } else if (d.message) {
          alert(d.message);
        }
        nav('/queue');
      } else {
        setOriginal({ subject, body });
      }
    } catch (e) {
      setErr(String(e.message || e));
    } finally {
      setBusy(false);
    }
  }

  if (err && !pitch) {
    return <Shell><PageHeader title="Pitch not found" sub={err} /></Shell>;
  }
  if (!pitch) {
    return <Shell><div className="row muted"><span className="spinner" /> Loading pitch</div></Shell>;
  }

  return (
    <Shell narrow>
      <PageHeader
        eyebrow={pitch.story_angle_name || pitch.story_angle || 'Pitch'}
        title="Edit draft"
        sub={`To ${pitch.prospect_name}${pitch.prospect_outlet ? ' at ' + pitch.prospect_outlet : ''}. Edits are tracked, the diff feeds future Claude drafts.`}
        right={
          <>
            <Score value={pitch.fit_score} />
            <Link to="/queue" className="btn btn-ghost btn-sm">Back to queue</Link>
          </>
        }
      />

      <div className="stack-6">
        <div className="card card-pad">
          <Field
            label="Subject"
            help={subjOver ? `${subjLen} / 70 — too long, will get truncated on mobile` : `${subjLen} / 70`}
            error={null}
            htmlFor="subject"
          >
            <Input id="subject" value={subject} onChange={e => setSubject(e.target.value)} className={subjOver ? 'input' : 'input'} style={subjOver ? { borderColor: 'var(--warning)' } : null} />
          </Field>
        </div>

        <div className="card card-pad">
          <Field
            label="Body"
            help={
              <span>
                {wordsOver ? <span style={{ color: 'var(--warning)' }}>{wordCount} words / 180 max</span> : <>{wordCount} / 180 words</>}
                {emDashCount > 0 ? <span style={{ color: 'var(--danger)', marginLeft: 12 }}>· {emDashCount} em/en dash{emDashCount === 1 ? '' : 'es'} found — replace before send</span> : null}
              </span>
            }
            htmlFor="body"
          >
            <Textarea id="body" value={body} onChange={e => setBody(e.target.value)} style={{ minHeight: 320, fontFamily: 'var(--font-body)', fontSize: 15, lineHeight: 1.6 }} />
          </Field>
          <p className="muted" style={{ fontSize: 12, marginTop: 8 }}>
            Editing tip: keep the first sentence specific to {pitch.prospect_name}'s recent work. Keep one concrete number. End with a warm closing line like "Talk soon," — the signature block (Adam Capps, Founder, phone, opt-out, address) is appended automatically when the email sends.
          </p>
        </div>

        {err ? (
          <div className="card card-pad" style={{ borderColor: 'var(--danger)' }}>
            <div style={{ color: 'var(--danger)' }}>{err}</div>
          </div>
        ) : null}

        <div className="row" style={{ gap: 12 }}>
          <button className="btn" onClick={() => save({ thenApprove: true })} disabled={busy || emDashCount > 0}>
            {busy ? <><span className="spinner" /> Saving</> : 'Save and approve'}
          </button>
          <button className="btn btn-secondary" onClick={() => save({ thenApprove: false })} disabled={busy || !dirty}>
            Save changes
          </button>
          <Link to="/queue" className="btn btn-ghost" style={{ marginLeft: 'auto' }}>Cancel</Link>
        </div>
      </div>
    </Shell>
  );
}
